import React, { useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import SaveStudioAssetButton from "../SaveStudioAssetButton";
import FieldHint from "../FieldHint";
import useI18n from "../../../i18n/useI18n";

import MotionSurface from "../../../motion/studio/MotionSurface";
import ToolResultSkeleton from "../../loaders/ToolResultSkeleton";

import ResultCard from "../../ui/ResultCard";
import ActionRow from "../../ui/ActionRow";
import CopyButton from "../../ui/CopyButton";

import AIActionButton from "../../ui/buttons/AIActionButton";
import UtilityButton from "../../ui/buttons/UtilityButton";

const STUDIO_USAGE_REFRESH_KEY = "hookora_studio_usage_refresh";
const THEME_KEY = "hookora_theme";

const getPreferredLanguage = () => {
  if (typeof window === "undefined") return "en";
  return localStorage.getItem("hookora_language") || "en";
};

const notifyStudioUsageUpdated = () => {
  const value = String(Date.now());
  localStorage.setItem(STUDIO_USAGE_REFRESH_KEY, value);
  window.dispatchEvent(new CustomEvent("hookora:studio-usage-updated"));
};

const formatStyleLabel = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return raw
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getScoreTone = (score) => {
  const safe = Number(score) || 0;
  if (safe >= 85) {
    return {
      bar: "#16a34a",
      pillBg: "rgba(22,163,74,0.14)",
      pillText: "#16a34a",
    };
  }
  if (safe >= 70) {
    return {
      bar: "#2563eb",
      pillBg: "rgba(37,99,235,0.14)",
      pillText: "#2563eb",
    };
  }
  return {
    bar: "#f59e0b",
    pillBg: "rgba(245,158,11,0.16)",
    pillText: "#b45309",
  };
};

const ScriptTool = ({
  sharedTopic,
  setSharedTopic,
  sharedPlatform,
  setSharedPlatform,
}) => {
  const { t } = useI18n();

  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [style, setStyle] = useState("direct-to-camera");
  const [audience, setAudience] = useState("");
  const [goal, setGoal] = useState("");
  const [offer, setOffer] = useState("");
  const [painPoint, setPainPoint] = useState("");
  const [desiredOutcome, setDesiredOutcome] = useState("");
  const [angle, setAngle] = useState("");
  const [callToAction, setCallToAction] = useState("");
  const [keywords, setKeywords] = useState("");
  const [brandVoice, setBrandVoice] = useState("");
  const [extraContext, setExtraContext] = useState("");

  const [scripts, setScripts] = useState([]);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const syncTheme = () => setTheme(localStorage.getItem(THEME_KEY) || "light");
    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);
    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      cardBgStrong: isDark ? "rgba(15,23,42,0.92)" : "#ffffff",
      border: isDark ? "rgba(148,163,184,0.22)" : "#e5e7eb",
      borderStrong: isDark ? "rgba(148,163,184,0.34)" : "#d1d5db",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#374151",
      inputBg: isDark ? "#0f172a" : "#ffffff",
      inputText: isDark ? "#f8fafc" : "#111827",
      trackBg: isDark ? "#1e293b" : "#e5e7eb",
      panelBg: isDark ? "rgba(30,41,59,0.55)" : "#f8fafc",
      softBg: isDark ? "rgba(30,41,59,0.85)" : "#f1f5f9",
      buttonBg: isDark ? "#e2e8f0" : "#111827",
      buttonText: isDark ? "#020617" : "#ffffff",
      secondaryButtonBg: isDark ? "#1e293b" : "#e5e7eb",
      secondaryButtonText: isDark ? "#f8fafc" : "#111827",
      dangerBg: isDark ? "rgba(127,29,29,0.25)" : "#fef2f2",
      dangerText: isDark ? "#fecaca" : "#b91c1c",
      label: isDark ? "#e2e8f0" : "#111827",
      heroBg: isDark
        ? "linear-gradient(135deg, rgba(30,41,59,0.84), rgba(15,23,42,0.96))"
        : "linear-gradient(135deg, #f8fafc, #ffffff)",
      heroBorder: isDark ? "rgba(59,130,246,0.24)" : "#bfdbfe",
      heroBadgeBg: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
      heroBadgeText: isDark ? "#bfdbfe" : "#1d4ed8",
      heroBadgeBorder: isDark ? "rgba(96,165,250,0.24)" : "#bfdbfe",
      shadow: isDark
        ? "0 16px 34px rgba(2,6,23,0.34)"
        : "0 12px 28px rgba(15,23,42,0.06)",
      shadowSoft: isDark
        ? "0 10px 24px rgba(2,6,23,0.22)"
        : "0 6px 18px rgba(15,23,42,0.05)",
    }),
    [isDark]
  );

  const inputStyle = {
    width: "100%",
    padding: "13px 14px",
    borderRadius: "12px",
    border: `1px solid ${colors.borderStrong}`,
    outline: "none",
    boxSizing: "border-box",
    background: colors.inputBg,
    color: colors.inputText,
    fontSize: "14px",
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: "90px",
    resize: "vertical",
  };

  const labelStyle = {
    display: "flex",
    alignItems: "center",
    marginBottom: "6px",
    color: colors.label,
    fontSize: "14px",
    fontWeight: 600,
  };

  const clearResults = () => {
    setScripts([]);
    setCopiedId(null);
    setError("");
  };

  const resetForm = () => {
    setSharedTopic("");
    setSharedPlatform("TikTok");
    setStyle("direct-to-camera");
    setAudience("");
    setGoal("");
    setOffer("");
    setPainPoint("");
    setDesiredOutcome("");
    setAngle("");
    setCallToAction("");
    setKeywords("");
    setBrandVoice("");
    setExtraContext("");
    setScripts([]);
    setCopiedId(null);
    setError("");
  };

  const generateScripts = async () => {
    if (!sharedTopic.trim()) {
      setError(t("studioTools.scriptTool.errors.topicRequired"));
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await API.post("/studio/scripts", {
        topic: sharedTopic.trim(),
        platform: sharedPlatform,
        style,
        audience,
        goal,
        offer,
        pain_point: painPoint,
        desired_outcome: desiredOutcome,
        angle,
        call_to_action: callToAction,
        keywords,
        brand_voice: brandVoice,
        extra_context: extraContext,
        language: getPreferredLanguage(),
      });

      setScripts(Array.isArray(response?.data?.scripts) ? response.data.scripts : []);
      setCopiedId(null);
      notifyStudioUsageUpdated();
    } catch (err) {
      console.error("Failed to generate scripts:", err);
      setError(
        err?.response?.data?.detail || t("studioTools.scriptTool.errors.generateFailed")
      );
    } finally {
      setLoading(false);
    }
  };

  const buildScriptText = (script) => {
    if (!script) return "";

    if (script.text) return script.text;

    return [
      script.hook ? `${t("studioTools.scriptTool.output.hookLabel")}: ${script.hook}` : "",
      script.body ? `${t("studioTools.scriptTool.output.bodyLabel")}: ${script.body}` : "",
      script.cta ? `${t("studioTools.scriptTool.output.ctaLabel")}: ${script.cta}` : "",
    ]
      .filter(Boolean)
      .join("\n\n");
  };

  const copyScript = async (script, id) => {
    try {
      await navigator.clipboard.writeText(buildScriptText(script));
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy script:", err);
    }
  };

  const renderScoreBar = (score) => {
    const safe = Number(score) || 0;
    const tone = getScoreTone(safe);
    return (
      <div
        style={{
          marginTop: "10px",
          height: "10px",
          borderRadius: "999px",
          background: colors.trackBg,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${Math.max(0, Math.min(100, safe))}%`,
            height: "100%",
            borderRadius: "999px",
            background: tone.bar,
          }}
        />
      </div>
    );
  };

  return (
    <MotionSurface>
    <div style={{ display: "grid", gap: "18px" }}>
      <div
        style={{
          border: `1px solid ${colors.heroBorder}`,
          borderRadius: "20px",
          padding: "18px",
          background: colors.heroBg,
          boxShadow: colors.shadow,
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "6px 10px",
            borderRadius: "999px",
            background: colors.heroBadgeBg,
            color: colors.heroBadgeText,
            border: `1px solid ${colors.heroBadgeBorder}`,
            fontSize: "12px",
            fontWeight: 800,
            letterSpacing: "0.03em",
            marginBottom: "12px",
          }}
        >
          {t("studioTools.scriptTool.hero.badge")}
        </div>

        <div style={{ color: colors.text, fontSize: "24px", fontWeight: 800, marginBottom: "8px" }}>
          {t("studioTools.scriptTool.hero.title")}
        </div>

        <div style={{ color: colors.textSoft, lineHeight: "1.7", maxWidth: "880px" }}>
          {t("studioTools.scriptTool.hero.description.before")}
          <strong>{t("studioTools.scriptTool.hero.description.strong")}</strong>
          {t("studioTools.scriptTool.hero.description.after")}
        </div>
      </div>

      <div
        style={{
          border: `1px solid ${colors.border}`,
          borderRadius: "18px",
          padding: "18px",
          background: colors.cardBg,
          boxShadow: colors.shadowSoft,
        }}
      >
        <div style={{ display: "grid", gap: "14px" }}>
          <div>
            <label style={labelStyle}>
              {t("studioTools.scriptTool.fields.topic.label")}
              <FieldHint text={t("studioTools.scriptTool.fields.topic.hint")} />
            </label>
            <textarea
              value={sharedTopic}
              onChange={(e) => setSharedTopic(e.target.value)}
              placeholder={t("studioTools.scriptTool.fields.topic.placeholder")}
              style={textareaStyle}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "12px",
            }}
          >
            <div>
              <label style={labelStyle}>
                {t("studioTools.scriptTool.fields.platform.label")}
                <FieldHint text={t("studioTools.scriptTool.fields.platform.hint")} />
              </label>
              <select
                value={sharedPlatform}
                onChange={(e) => setSharedPlatform(e.target.value)}
                style={inputStyle}
              >
                {[
                  "TikTok",
                  "Instagram Reels",
                  "YouTube Shorts",
                  "Instagram",
                  "YouTube",
                  "LinkedIn",
                ].map((platform) => (
                  <option key={platform} value={platform}>
                    {platform}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>
                {t("studioTools.scriptTool.fields.style.label")}
                <FieldHint text={t("studioTools.scriptTool.fields.style.hint")} />
              </label>
              <select value={style} onChange={(e) => setStyle(e.target.value)} style={inputStyle}>
                {[
                  "direct-to-camera",
                  "story-led",
                  "problem-solution",
                  "myth-busting",
                ].map((item) => (
                  <option key={item} value={item}>
                    {t(`studioTools.scriptTool.styleOptions.${item}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            style={{
              padding: "14px",
              borderRadius: "14px",
              background: colors.panelBg,
              border: `1px solid ${colors.border}`,
            }}
          >
            <div style={{ fontWeight: 700, color: colors.text, marginBottom: "12px" }}>
              {t("studioTools.scriptTool.context.title")}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "12px",
              }}
            >
              <div>
                <label style={labelStyle}>
                  {t("studioTools.scriptTool.fields.audience.label")}
                  <FieldHint text={t("studioTools.scriptTool.fields.audience.hint")} />
                </label>
                <input
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder={t("studioTools.scriptTool.fields.audience.placeholder")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.scriptTool.fields.goal.label")}
                  <FieldHint text={t("studioTools.scriptTool.fields.goal.hint")} />
                </label>
                <input
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  placeholder={t("studioTools.scriptTool.fields.goal.placeholder")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.scriptTool.fields.offer.label")}
                  <FieldHint text={t("studioTools.scriptTool.fields.offer.hint")} />
                </label>
                <input
                  value={offer}
                  onChange={(e) => setOffer(e.target.value)}
                  placeholder={t("studioTools.scriptTool.fields.offer.placeholder")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.scriptTool.fields.painPoint.label")}
                  <FieldHint text={t("studioTools.scriptTool.fields.painPoint.hint")} />
                </label>
                <input
                  value={painPoint}
                  onChange={(e) => setPainPoint(e.target.value)}
                  placeholder={t("studioTools.scriptTool.fields.painPoint.placeholder")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.scriptTool.fields.desiredOutcome.label")}
                  <FieldHint text={t("studioTools.scriptTool.fields.desiredOutcome.hint")} />
                </label>
                <input
                  value={desiredOutcome}
                  onChange={(e) => setDesiredOutcome(e.target.value)}
                  placeholder={t("studioTools.scriptTool.fields.desiredOutcome.placeholder")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.scriptTool.fields.angle.label")}
                  <FieldHint text={t("studioTools.scriptTool.fields.angle.hint")} />
                </label>
                <input
                  value={angle}
                  onChange={(e) => setAngle(e.target.value)}
                  placeholder={t("studioTools.scriptTool.fields.angle.placeholder")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.scriptTool.fields.callToAction.label")}
                  <FieldHint text={t("studioTools.scriptTool.fields.callToAction.hint")} />
                </label>
                <input
                  value={callToAction}
                  onChange={(e) => setCallToAction(e.target.value)}
                  placeholder={t("studioTools.scriptTool.fields.callToAction.placeholder")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.scriptTool.fields.keywords.label")}
                  <FieldHint text={t("studioTools.scriptTool.fields.keywords.hint")} />
                </label>
                <input
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  placeholder={t("studioTools.scriptTool.fields.keywords.placeholder")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.scriptTool.fields.brandVoice.label")}
                  <FieldHint text={t("studioTools.scriptTool.fields.brandVoice.hint")} />
                </label>
                <input
                  value={brandVoice}
                  onChange={(e) => setBrandVoice(e.target.value)}
                  placeholder={t("studioTools.scriptTool.fields.brandVoice.placeholder")}
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginTop: "12px" }}>
              <label style={labelStyle}>
                {t("studioTools.scriptTool.fields.extraContext.label")}
                <FieldHint text={t("studioTools.scriptTool.fields.extraContext.hint")} />
              </label>
              <textarea
                value={extraContext}
                onChange={(e) => setExtraContext(e.target.value)}
                placeholder={t("studioTools.scriptTool.fields.extraContext.placeholder")}
                style={textareaStyle}
              />
            </div>
          </div>

          {error && (
            <div
              style={{
                padding: "12px 14px",
                borderRadius: "12px",
                background: colors.dangerBg,
                color: colors.dangerText,
              }}
            >
              {error}
            </div>
          )}

          <ActionRow>
            <AIActionButton isDark={isDark}
              onClick={generateScripts}
              disabled={loading}
            >
              {loading
                ? t("studioTools.scriptTool.actions.generating")
                : t("studioTools.scriptTool.actions.generate")}
            </AIActionButton>

            <UtilityButton isDark={isDark}
              type="button"
              onClick={clearResults}
            >
              {t("studioTools.scriptTool.actions.clear")}
            </UtilityButton>

            <UtilityButton isDark={isDark}
              type="button"
              onClick={resetForm}
            >
              {t("studioTools.scriptTool.actions.reset")}
            </UtilityButton>
          </ActionRow>
        </div>
      </div>

      {!!scripts.length && (
        <div
          style={{
            color: colors.textMuted,
            fontSize: "14px",
            lineHeight: "1.6",
            marginTop: "-4px",
          }}
        >
          {t("studioTools.scriptTool.output.helper.before")}
          <strong>{t("studioTools.scriptTool.output.helper.strong")}</strong>
          {t("studioTools.scriptTool.output.helper.after")}
        </div>
      )}

      <div style={{ display: "grid", gap: "14px" }}>
        {loading ? (
          <ToolResultSkeleton isDark={isDark} count={3} />
        ) : (
        scripts.map((script, index) => {
          const safeScore = Number(script.score) || 0;
          const tone = getScoreTone(safeScore);
          const fullScriptText = buildScriptText(script);

          return (
            <ResultCard
              key={`${script.title}-${index}`}
              colors={colors}
              index={index}
            >
              <div style={{ color: colors.text, fontWeight: 700, fontSize: "18px" }}>
                {script.title}
              </div>

              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "10px" }}>
                {script.style && (
                  <span
                    style={{
                      background: colors.softBg,
                      borderRadius: "999px",
                      padding: "6px 10px",
                      fontSize: "12px",
                      color: colors.textMuted,
                      fontWeight: 700,
                    }}
                  >
                    {formatStyleLabel(script.style)}
                  </span>
                )}

                <span
                  style={{
                    background: tone.pillBg,
                    borderRadius: "999px",
                    padding: "6px 10px",
                    fontSize: "12px",
                    color: tone.pillText,
                    fontWeight: 700,
                  }}
                >
                  {t("studioTools.scriptTool.output.score")}: {safeScore}
                </span>
              </div>

              <div style={{ display: "grid", gap: "10px", marginTop: "12px" }}>
                <div>
                  <div style={{ color: colors.textMuted, fontSize: "12px", marginBottom: "4px" }}>
                    {t("studioTools.scriptTool.output.hookLabel")}
                  </div>
                  <div style={{ color: colors.text, lineHeight: "1.6" }}>{script.hook}</div>
                </div>

                <div>
                  <div style={{ color: colors.textMuted, fontSize: "12px", marginBottom: "4px" }}>
                    {t("studioTools.scriptTool.output.bodyLabel")}
                  </div>
                  <div style={{ color: colors.text, lineHeight: "1.6" }}>{script.body}</div>
                </div>

                <div>
                  <div style={{ color: colors.textMuted, fontSize: "12px", marginBottom: "4px" }}>
                    {t("studioTools.scriptTool.output.ctaLabel")}
                  </div>
                  <div style={{ color: colors.text, lineHeight: "1.6" }}>{script.cta}</div>
                </div>
              </div>

              {renderScoreBar(safeScore)}

              <ActionRow style={{ marginTop: "14px" }}>
                <CopyButton
                  copied={copiedId === index}
                  onClick={() => copyScript(script, index)}
                >
                  {copiedId === index
                    ? t("studioTools.scriptTool.actions.copied")
                    : t("studioTools.scriptTool.actions.copy")}
                </CopyButton>

                <SaveStudioAssetButton
                  toolType="scripts"
                  title={`${script.title} • ${sharedTopic || t("studioTools.scriptTool.output.untitledTopic")}`}
                  topic={sharedTopic}
                  platform={sharedPlatform}
                  content={fullScriptText}
                  meta={{
                    score: script.score,
                    label: script.label,
                    style: script.style || "",
                  }}
                />
              </ActionRow>
            </ResultCard>
          );
        })
        )}
      </div>
    </div>
    </MotionSurface>
  );
};

export default ScriptTool;